import { Loader, PinInput, UnstyledButton } from '@mantine/core';
import { IconMail, IconPhone } from '@tabler/icons-react';
import { Endpoints } from '@xver/apis';
import type { OAuthLinkChallenge } from '@xver/apis/v1/authentication/model';
import Button from '@xver/components/Atoms/Button/Button';
import usePageHeading from '@xver/hooks/usePageHeading';
import { useResendCountdown } from '@xver/pages/public/AuthenticationPage/components/PhoneNumberVerificationForm/hooks/useResendCountdown';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_MS = 300_000;

type Channel = 'phone' | 'email';
type VerifyOutcome = 'linked' | 'otp' | 'error';

interface OAuthLinkVerificationProps {
    challenge: OAuthLinkChallenge;
    onLinked: (token: string) => void;
    onCancel: () => void;
}

export default function OAuthLinkVerification({
    challenge,
    onLinked,
    onCancel,
}: OAuthLinkVerificationProps) {
    const { t } = useTranslation('auth');
    const headingRef = usePageHeading(t('oauthLink.title'));

    const channels = challenge.channels ?? [];
    const singleChannel = channels.length === 1;

    const [step, setStep] = useState<'choose' | 'otp'>(singleChannel ? 'otp' : 'choose');
    const [channel, setChannel] = useState<Channel | ''>(singleChannel ? channels[0] : '');
    const [otpCode, setOtpCode] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const { isAvailable, formattedRemainingTime, restart } = useResendCountdown({
        initialDurationMs: RESEND_COOLDOWN_MS,
    });

    const verify = useCallback(
        async (payload: { channel: Channel; otpCode?: string }): Promise<VerifyOutcome> => {
            setSubmitting(true);
            setError(null);
            try {
                const res = await Endpoints.v1.Authentication.oauthLinkVerify({
                    challengeToken: challenge.challengeToken,
                    ...payload,
                });
                const data = res?.data ?? {};
                if (data.token) {
                    onLinked(data.token);
                    return 'linked';
                }
                if (data.requiresOTP) {
                    setStep('otp');
                    return 'otp';
                }
                setError(t('oauthLink.verifyFailed', 'Verification failed. Please try again.'));
                return 'error';
            } catch (err) {
                const message = (err as { message?: string })?.message;
                setError(
                    message || t('oauthLink.verifyFailed', 'Verification failed. Please try again.')
                );
                return 'error';
            } finally {
                setSubmitting(false);
            }
        },
        [challenge.challengeToken, onLinked, t]
    );

    // Single-channel accounts skip the chooser: send the code once on mount.
    const didSendRef = useRef(false);
    useEffect(() => {
        if (!singleChannel || didSendRef.current) return;
        didSendRef.current = true;
        void verify({ channel: channels[0] }).then((outcome) => {
            if (outcome === 'otp') restart();
        });
    }, [singleChannel, channels, verify, restart]);

    const chooseChannel = useCallback(
        async (ch: Channel) => {
            if (submitting) return;
            setChannel(ch);
            setOtpCode('');
            const outcome = await verify({ channel: ch });
            if (outcome === 'otp') restart();
        },
        [submitting, verify, restart]
    );

    const handleOtpChange = useCallback(
        (value: string) => {
            setOtpCode(value);
            if (value.length === OTP_LENGTH && channel && !submitting) {
                void verify({ channel, otpCode: value }).then((outcome) => {
                    if (outcome === 'error') setOtpCode('');
                });
            }
        },
        [channel, submitting, verify]
    );

    const handleResend = useCallback(async () => {
        if (!isAvailable || submitting || !channel) return;
        const outcome = await verify({ channel });
        if (outcome !== 'error') restart();
    }, [isAvailable, submitting, channel, verify, restart]);

    const changeMethod = useCallback(() => {
        setStep('choose');
        setOtpCode('');
        setError(null);
    }, []);

    const isVerifyingOtp = submitting && otpCode.length === OTP_LENGTH;

    return (
        <form
            autoComplete="off"
            onSubmit={(e) => e.preventDefault()}
            className="relative w-full max-w-sm flex flex-col justify-center items-center gap-2"
        >
            <div className="w-full flex flex-col justify-center items-center">
                <h1 ref={headingRef} tabIndex={-1} className="text-base font-bold text-fg">
                    {t('oauthLink.title')}
                </h1>
                <span className="text-sm text-fg/78 text-center">
                    {t('oauthLink.subtitle', { email: challenge.email })}
                </span>
            </div>

            {step === 'choose' ? (
                <div className="w-full flex flex-col gap-3 my-4">
                    <span className="text-sm text-fg/78 text-center">
                        {t('oauthLink.chooseSubtitle')}
                    </span>
                    {channels.includes('phone') && (
                        <Button
                            type="button"
                            size="sm"
                            className="w-full"
                            leftIcon={<IconPhone size={16} />}
                            isLoading={submitting}
                            onClick={() => chooseChannel('phone')}
                        >
                            {t('oauthLink.viaPhone')}
                        </Button>
                    )}
                    {channels.includes('email') && (
                        <Button
                            type="button"
                            size="sm"
                            variant="light"
                            className="w-full"
                            leftIcon={<IconMail size={16} />}
                            isLoading={submitting}
                            onClick={() => chooseChannel('email')}
                        >
                            {t('oauthLink.viaEmail')}
                        </Button>
                    )}
                    {error && (
                        <p className="text-red-400 light:text-red-700 text-sm text-center">
                            {error}
                        </p>
                    )}
                </div>
            ) : (
                <div className="w-full flex flex-col justify-center items-center gap-6 my-4">
                    <span className="text-sm text-fg/78 text-center">
                        {channel === 'phone'
                            ? t('oauthLink.otpSentPhone')
                            : t('oauthLink.otpSentEmail')}
                    </span>

                    <PinInput
                        size={'sm'}
                        length={OTP_LENGTH}
                        value={otpCode}
                        onChange={handleOtpChange}
                        disabled={submitting}
                        styles={{ input: { textAlign: 'center' } }}
                    />

                    {error && <p className="text-red-400 light:text-red-700 text-sm">{error}</p>}

                    <div className="inline-flex items-center gap-1">
                        <span className="text-[14px] text-fg/78">
                            {t('oauthLink.didntReceive')}
                        </span>
                        <UnstyledButton
                            onClick={handleResend}
                            disabled={!isAvailable || submitting}
                        >
                            <span
                                className={`text-[14px] ${
                                    isAvailable && !submitting
                                        ? 'text-blue-400 light:text-blue-700 hover:underline cursor-pointer'
                                        : 'cursor-not-allowed text-fg/50'
                                }`}
                            >
                                {isAvailable
                                    ? t('oauthLink.resend')
                                    : t('oauthLink.resendCountdown', {
                                          countdown: formattedRemainingTime,
                                      })}
                            </span>
                        </UnstyledButton>
                    </div>

                    {!singleChannel && (
                        <UnstyledButton onClick={changeMethod} disabled={submitting}>
                            <span className="text-[14px] text-blue-400 light:text-blue-700 hover:underline cursor-pointer">
                                {t('oauthLink.changeMethod')}
                            </span>
                        </UnstyledButton>
                    )}
                </div>
            )}

            <div className="w-full flex flex-row justify-center items-center gap-2">
                <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="w-full"
                    onClick={onCancel}
                >
                    {t('oauthLink.cancel')}
                </Button>
            </div>

            {isVerifyingOtp && (
                <div className="absolute inset-0 z-10 flex justify-center items-center backdrop-blur-sm bg-black/30">
                    <Loader type="bars" size={'md'} />
                </div>
            )}
        </form>
    );
}
